import { useEffect, useState } from 'react';
import { Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

/**
 * Admin read-only panel — recent Vision Deck retargeting events across
 * all tenants. Backed by GET /api/admin/retargeting/events.
 * Silent-on-failure so it can never break /admin/users.
 */
export default function RetargetingEventsTile({ token, API }) {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/retargeting/events?limit=20`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setEvents(data.events || []);
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, [API, token]);

  if (!events) return null;

  const statusChip = (evt) => {
    const s = evt.status || (evt.capi_success ? 'sent' : 'failed');
    const styles = {
      sent: 'bg-emerald-100 text-emerald-800',
      processing: 'bg-blue-100 text-blue-800',
      skipped: 'bg-neutral-100 text-neutral-600',
      failed: 'bg-rose-100 text-rose-800',
    };
    return (
      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${styles[s] || styles.skipped}`}>
        {s === 'skipped' ? `skipped (${evt.skipped_reason || 'not-opted-in'})` : s}
      </span>
    );
  };

  return (
    <Card data-testid="retargeting-events-tile">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Target className="h-4 w-4 text-amber-700" />
          Retargeting Events
          <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
            {events.length} recent
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">
            No retargeting events yet. Enable retargeting in a Professional+ account's Settings
            and open a public Vision Deck link to trigger the first event.
          </p>
        ) : (
          <div className="divide-y" data-testid="retargeting-events-list">
            {events.map((evt) => (
              <div key={evt.id} className="grid grid-cols-12 gap-2 py-2 items-center text-sm">
                <div className="col-span-4 truncate">
                  <div className="font-medium truncate" title={evt.deck_title}>{evt.deck_title}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {evt.owner?.email || evt.user_id?.slice(0, 12)}
                  </div>
                </div>
                <div className="col-span-3 text-xs text-muted-foreground truncate" title={evt.recipient_email || ''}>
                  {evt.recipient_email || <span className="italic">unknown recipient</span>}
                </div>
                <div className="col-span-3 text-xs text-muted-foreground">
                  {evt.viewed_at ? new Date(evt.viewed_at).toLocaleString() : '—'}
                </div>
                <div className="col-span-2 text-right">
                  {statusChip(evt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
